import { Component, ChangeDetectionStrategy, effect, inject, input, output } from '@angular/core';
import { DynamicLocaleService } from '../../../dynamic-locale.service';
import { GroupBase } from '../../../models/blueprint.model';
import {
  GroupedDataRecordModel,
  RecordUpdateEmission,
  ReportOptions,
  ReportView,
  TemplateProjectionMap,
} from '../../../models/report.model';
import { TemplateField } from '../../../models/template.model';
import { IMAGE_FIELD_ID } from '../../../shared';
import { EditableFieldsService } from '../../editable-fields.service';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MiqImagesModule } from '@miq/utils';
import { ReportFieldFormatterPipe } from '../../../shared/pipes/report-field-formatter.pipe';
import { SelectTemplateOutletPipe } from '../../../shared/pipes/select-template-outlet.pipe';
import { ReportRecordRouteFnPipe } from '../../../shared/pipes/report-record-route-fn.pipe';
import { DisplayFieldPipe } from '../display.pipe';
import { IsFieldVisiblePipe } from '../../../shared/pipes/field-visible.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rep-condensed-body',
  templateUrl: './condensed-body.component.html',
  styleUrl: './condensed-body.component.scss',
  imports: [
    NgClass,
    MatIcon,
    NgTemplateOutlet,
    MiqImagesModule,
    AsyncPipe,
    ReportFieldFormatterPipe,
    SelectTemplateOutletPipe,
    ReportRecordRouteFnPipe,
    DisplayFieldPipe,
    IsFieldVisiblePipe
],
})
export class CondensedBodyComponent {
  public editableFieldsService = inject(EditableFieldsService);
  private readonly dynamicLocaleService = inject(DynamicLocaleService);

  readonly entity = input.required<string>();
  readonly reportRecordData = input.required<GroupedDataRecordModel>();
  readonly bodyGroups = input.required<GroupBase<TemplateField>[]>();
  readonly footerGroups = input.required<GroupBase<TemplateField>[]>();
  readonly currentView = input.required<ReportView>();
  readonly templateProjectionMap = input.required<TemplateProjectionMap>();
  readonly reportOptions = input.required<ReportOptions>();
  readonly isPDF = input<boolean>(false);

  readonly removeRecord = output<void>();
  readonly reportUpdateClick = output<RecordUpdateEmission>();

  /** Local mutable copy since signal inputs are readonly */
  bodyGroupsLocal: GroupBase<TemplateField>[];
  public imageField: TemplateField;

  constructor() {
    effect(() => {
      const bodyGroups = this.bodyGroups();
      const footerGroups = this.footerGroups();
      const reportOptions = this.reportOptions();
      this.editableFieldsService.mapEditableFields(bodyGroups);
      this.editableFieldsService.mapEditableFields(footerGroups);
      const bodyFields: TemplateField[] = bodyGroups.reduce((acc, group) => [...acc, ...group.fields], []) || [];
      this.bodyGroupsLocal = bodyGroups.filter(group => !group.fields.some(field => field.id === IMAGE_FIELD_ID));
      this.imageField = bodyFields.find(field => field.id === IMAGE_FIELD_ID);
      // hide broker or agency on check in report options
      this.hideBrokerOnCheck(reportOptions);
    });
  }

  emitEditableFields(event: MouseEvent, id: number) {
    const emittedObject = this.editableFieldsService.generateRecordUpdateEmission(
      event,
      id,
      this.reportRecordData().data[0]
    );
    if (emittedObject != null) {
      emittedObject.locale = this.dynamicLocaleService.reportLocale;
      this.reportUpdateClick.emit(emittedObject);
    }
  }

  private hideBrokerOnCheck(reportOptions: ReportOptions) {
    const { brokerAgency } = reportOptions;
    if (brokerAgency) {
      this.bodyGroupsLocal = this.bodyGroupsLocal.filter(group => !group.specialCase);
    }
  }
}
